import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  LoaderCircle,
  Save,
  ShieldCheck,
} from 'lucide-react';
import { api } from '../lib/api';

type CoverSpec = {
  ready: boolean;
  reason?: string;
  trimWidthIn?: number;
  trimHeightIn?: number;
  spineWidthIn?: number;
  coverWidthIn?: number;
  coverHeightIn?: number;
  spineTextEligible?: boolean;
};

type CoverForm = {
  title: string;
  subtitle: string;
  creator: string;
  backDescription: string;
  frontBackground: string;
  backBackground: string;
  spineBackground: string;
};

export default function FullWrapCoverDesigner({
  projectId,
  projectTitle,
}: {
  projectId: string;
  projectTitle: string;
}) {
  const [spec, setSpec] = useState<CoverSpec | null>(null);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState<CoverForm>({
    title: projectTitle,
    subtitle: '',
    creator: '',
    backDescription: '',
    frontBackground: '#ef5b3e',
    backBackground: '#17191d',
    spineBackground: '#201d1a',
  });

  useEffect(() => {
    api
      .get<CoverSpec>(`/api/projects/${projectId}/publish/cover-spec`)
      .then(({ data }) => setSpec(data))
      .catch(() => setMessage('Cover dimensions could not be loaded.'));
  }, [projectId]);

  const previewRatio = useMemo(() => {
    if (!spec?.ready || !spec.coverWidthIn || !spec.coverHeightIn) return 1.4;
    return spec.coverWidthIn / spec.coverHeightIn;
  }, [spec]);

  const saveAndRender = async () => {
    setBusy('render');
    setMessage('');
    try {
      await api.put(`/api/projects/${projectId}/publish/cover-layout`, form);
      const { data } = await api.post<{ widthIn: number; heightIn: number }>(
        `/api/projects/${projectId}/publish/render-cover`
      );
      setMessage(`Cover PDF ready · ${data.widthIn.toFixed(3)} × ${data.heightIn.toFixed(3)} in`);
    } catch {
      setMessage('The cover could not be rendered. Check the print setup.');
    } finally {
      setBusy('');
    }
  };

  if (spec && !spec.ready) {
    return (
      <section className="card p-6 sm:p-8">
        <ShieldCheck size={28} />
        <h3 className="mt-3 text-xl font-black">Cover setup needs one more choice.</h3>
        <p className="mt-2 text-sm text-black/50">{spec.reason}</p>
      </section>
    );
  }

  return (
    <section className="card p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="eyebrow text-[#ef5b3e]">FULL-WRAP COVER</span>
          <h2 className="mt-2 text-3xl font-black">Front + spine + back.</h2>
          <p className="mt-2 text-sm text-black/50">
            Dimensions are calculated from the actual interior page count and
            the project's chosen print setup.
          </p>
        </div>
        <button
          onClick={saveAndRender}
          disabled={busy === 'render' || !spec}
          className="primary"
        >
          {busy === 'render' ? (
            <LoaderCircle size={17} className="animate-spin" />
          ) : (
            <Save size={17} />
          )}
          {busy === 'render' ? 'Rendering…' : 'Save & render cover'}
        </button>
      </div>

      <div className="mt-7 grid gap-6 xl:grid-cols-[1fr_380px]">
        <div>
          <div
            className="relative mx-auto overflow-hidden rounded-xl border border-black/15 shadow-xl"
            style={{
              width: '100%',
              aspectRatio: String(previewRatio),
              background: form.backBackground,
            }}
          >
            <div
              className="absolute inset-y-0 left-1/2 -translate-x-1/2"
              style={{
                width: `${Math.max(
                  1.5,
                  ((spec?.spineWidthIn || 0.1) / (spec?.coverWidthIn || 14)) * 100
                )}%`,
                background: form.spineBackground,
              }}
            />
            <div
              className="absolute inset-y-0 right-0 flex w-[48%] items-center justify-center p-8 text-center text-white"
              style={{ background: form.frontBackground }}
            >
              <div>
                <div className="text-3xl font-black">{form.title}</div>
                {form.subtitle && (
                  <div className="mt-2 text-sm font-bold opacity-80">
                    {form.subtitle}
                  </div>
                )}
                {form.creator && (
                  <div className="mt-8 text-xs font-black uppercase tracking-widest">
                    {form.creator}
                  </div>
                )}
              </div>
            </div>
            <div className="absolute inset-y-0 left-0 flex w-[48%] items-center p-8 text-white">
              <p className="max-w-[70%] text-sm leading-relaxed opacity-85">
                {form.backDescription || 'Back-cover description…'}
              </p>
              <div className="absolute bottom-[7%] right-[6%] h-[18%] w-[28%] rounded bg-white/90 text-[9px] font-black text-black grid place-items-center">
                BARCODE SAFE
              </div>
            </div>
            {spec?.spineTextEligible && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-90 whitespace-nowrap text-[10px] font-black text-white">
                {form.title}
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap justify-between gap-2 text-xs text-black/40">
            <span>
              {spec?.coverWidthIn?.toFixed(3)} × {spec?.coverHeightIn?.toFixed(3)} in
            </span>
            <span>
              Spine {spec?.spineWidthIn?.toFixed(3)} in
            </span>
          </div>
        </div>

        <aside className="space-y-4">
          <Field label="Title">
            <input
              value={form.title}
              onChange={event => setForm({ ...form, title: event.target.value })}
            />
          </Field>
          <Field label="Subtitle">
            <input
              value={form.subtitle}
              onChange={event =>
                setForm({ ...form, subtitle: event.target.value })
              }
            />
          </Field>
          <Field label="Creator">
            <input
              value={form.creator}
              onChange={event =>
                setForm({ ...form, creator: event.target.value })
              }
            />
          </Field>
          <Field label="Back description">
            <textarea
              rows={6}
              value={form.backDescription}
              onChange={event =>
                setForm({ ...form, backDescription: event.target.value })
              }
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <ColorField
              label="Front"
              value={form.frontBackground}
              onChange={value => setForm({ ...form, frontBackground: value })}
            />
            <ColorField
              label="Spine"
              value={form.spineBackground}
              onChange={value => setForm({ ...form, spineBackground: value })}
            />
            <ColorField
              label="Back"
              value={form.backBackground}
              onChange={value => setForm({ ...form, backBackground: value })}
            />
          </div>
        </aside>
      </div>

      {message && (
        <div className="mt-5 rounded-2xl bg-[#f1eee6] p-4 text-sm text-black/60">
          {message}
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-black uppercase tracking-[.12em] text-black/45">
        {label}
      </span>
      {children}
    </label>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-center text-xs font-black text-black/45">
      <input
        type="color"
        value={value}
        onChange={event => onChange(event.target.value)}
        className="mb-1 h-12 p-1"
      />
      {label}
    </label>
  );
}
